<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { api } from '../../framework/js/api.js';
  import {
    AppEmptyState,
    AppLoadingState,
    AppErrorState,
    AppToolbar,
    SortControl,
    StatusPills,
    AssessmentTile,
    AssessmentPager,
    relativeTime,
    matchesSearch,
    paginate,
  } from '../../framework/components/app';
  import type { RingSpec, RingTone, StatusTone, PillOption } from '../../framework/components/app';

  const dispatch = createEventDispatcher();

  interface BriefRow {
    uuid: string;
    name?: string | null;
    created_at?: string;
    brief_created_at?: string;
    updated_at?: string;
    last_generated?: string | null;
    version_count?: number;
    alignment_percent?: number | null;
    alignment_status?: string | null;
    has_response?: boolean;
  }

  let briefs: BriefRow[] = [];
  let state: 'loading' | 'ready' | 'error' = 'loading';
  let searchQuery = '';
  let statusFilter: string[] = [];
  let sortField: 'newest' | 'oldest' | 'titleAsc' | 'titleDesc' = 'newest';
  let page = 1;
  let pageSize = 20;

  const STATUS_OPTIONS: PillOption[] = [
    { value: 'generated', label: 'Generated' },
    { value: 'draft', label: 'In progress' },
  ];

  const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'titleAsc', label: 'A – Z' },
    { value: 'titleDesc', label: 'Z – A' },
  ];

  function briefTitle(b: BriefRow): string {
    return b.name || 'Untitled brief';
  }

  function briefCreatedAt(b: BriefRow): string {
    return b.brief_created_at || b.created_at || '';
  }

  function briefActivityAt(b: BriefRow): string {
    return b.last_generated || b.updated_at || briefCreatedAt(b);
  }

  function briefStatus(b: BriefRow): 'generated' | 'draft' {
    return (b.version_count ?? 0) > 0 || b.has_response ? 'generated' : 'draft';
  }

  function alignmentTone(percent: number | null | undefined): RingTone {
    if (percent == null) return 'neutral';
    if (percent >= 75) return 'success';
    if (percent >= 60) return 'warn';
    return 'danger';
  }

  function tileFor(b: BriefRow): {
    ring: RingSpec;
    ringTone: RingTone;
    statusLabel: string;
    statusTone: StatusTone;
    statusDetail: string;
    subtitle: string;
    footerNote: string;
    versionChip: string;
    href: string;
    resumeHref: string;
  } {
    const vc = b.version_count ?? 0;
    const isGenerated = vc > 0 || !!b.has_response;
    const versionLabel = vc === 1 ? '1 version' : `${vc} versions`;

    if (isGenerated) {
      const percent = b.alignment_percent;
      return {
        ring:
          percent != null
            ? { kind: 'percent', value: percent }
            : { kind: 'icon', name: 'check' },
        // Ring reflects alignment quality; status badge is always success for Generated.
        ringTone: percent != null ? alignmentTone(percent) : 'success',
        statusLabel: 'Generated',
        statusTone: 'success',
        statusDetail: vc > 0 ? versionLabel : 'Brief ready',
        subtitle: `Created ${relativeTime(briefCreatedAt(b))}`,
        footerNote: b.last_generated
          ? `Generated ${relativeTime(b.last_generated)}`
          : '',
        versionChip: vc > 0 ? `v${vc}` : '',
        href: `/brief-builder/${b.uuid}`,
        resumeHref: '',
      };
    }

    return {
      ring: { kind: 'icon', name: 'pencil' },
      ringTone: 'warn',
      statusLabel: 'In progress',
      statusTone: 'warn',
      statusDetail: 'Continue consultation',
      subtitle: `Created ${relativeTime(briefCreatedAt(b))}`,
      footerNote: `Edited ${relativeTime(b.updated_at || briefCreatedAt(b))}`,
      versionChip: '',
      href: `/brief-builder/${b.uuid}`,
      resumeHref: `/brief-builder/${b.uuid}/continue`,
    };
  }

  function compareItems(a: BriefRow, b: BriefRow, field: typeof sortField): number {
    if (field === 'newest') {
      return new Date(briefActivityAt(b)).getTime() - new Date(briefActivityAt(a)).getTime();
    }
    if (field === 'oldest') {
      return new Date(briefActivityAt(a)).getTime() - new Date(briefActivityAt(b)).getTime();
    }
    if (field === 'titleDesc') {
      return briefTitle(b).localeCompare(briefTitle(a));
    }
    return briefTitle(a).localeCompare(briefTitle(b));
  }

  async function load() {
    state = 'loading';
    try {
      const res: any = await api.listBriefBuilders(searchQuery.length >= 3 ? searchQuery : '');
      if (!res?.success) {
        state = 'error';
        return;
      }
      briefs = res.data?.list || [];
      state = 'ready';
      dispatch('changed');
    } catch {
      state = 'error';
    }
  }

  function onSearch(e: CustomEvent<string>) {
    searchQuery = e.detail;
    page = 1;
    if (searchQuery.length === 0 || searchQuery.length >= 3) load();
  }

  function onStatusChange(e: CustomEvent<string[]>) {
    statusFilter = e.detail;
    page = 1;
  }

  function onSort(e: CustomEvent<{ field: string }>) {
    sortField = e.detail.field as typeof sortField;
    page = 1;
  }

  $: filtered = briefs.filter((b) => {
    if (statusFilter.length > 0 && !statusFilter.includes(briefStatus(b))) return false;
    if (searchQuery.length >= 3) return matchesSearch(briefTitle(b), searchQuery);
    return true;
  });

  $: sorted = [...filtered].sort((a, b) => compareItems(a, b, sortField));
  $: totalCount = sorted.length;
  $: pagedItems = paginate(sorted, page, pageSize);

  $: statusCounts = STATUS_OPTIONS.map((o) => ({
    ...o,
    count: briefs.filter((b) => briefStatus(b) === o.value).length,
  }));

  onMount(load);
</script>

{#if state === 'loading'}
  <AppLoadingState label="Loading briefs" />
{:else if state === 'error'}
  <AppErrorState on:retry={load} />
{:else if briefs.length === 0}
  <AppEmptyState
    icon="file text"
    title="No briefs yet"
    description="Start a ten-minute consultation to build your first brief."
    actionLabel="Begin"
    actionHref="/brief-builder/new"
  />
{:else}
  <div class="filter-bar">
    <AppToolbar
      bind:searchValue={searchQuery}
      searchPlaceholder="Search by project name…"
      searchLabel="Search briefs"
      resultsCount={totalCount}
      resultsSingular="brief"
      resultsPlural="briefs"
      on:search={onSearch}
    >
      <SortControl
        slot="controls"
        options={SORT_OPTIONS}
        field={sortField}
        on:change={onSort}
      />
    </AppToolbar>
    <StatusPills
      options={statusCounts}
      selected={statusFilter}
      on:change={onStatusChange}
    />
  </div>

  {#if pagedItems.length === 0}
    <div class="empty-filter">
      <p>No briefs match your filters.</p>
      <button
        type="button"
        class="reset-btn"
        on:click={() => {
          searchQuery = '';
          statusFilter = [];
          page = 1;
          load();
        }}
      >
        Clear filters
      </button>
    </div>
  {:else}
    <div class="tile-grid">
      {#each pagedItems as brief (brief.uuid)}
        {@const t = tileFor(brief)}
        <AssessmentTile
          agent="brief"
          href={t.href}
          title={briefTitle(brief)}
          subtitle={t.subtitle}
          ring={t.ring}
          ringTone={t.ringTone}
          statusLabel={t.statusLabel}
          statusTone={t.statusTone}
          statusDetail={t.statusDetail}
          versionChip={t.versionChip}
          footerNote={t.footerNote}
          resumeHref={t.resumeHref}
        />
      {/each}
    </div>
  {/if}

  <AssessmentPager bind:page bind:pageSize {totalCount} />
{/if}

<style>
  .filter-bar {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-bottom: var(--space-5);
  }

  .tile-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-6);
  }

  @media (max-width: 1440px) {
    .tile-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 1024px) {
    .tile-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 640px) {
    .tile-grid {
      grid-template-columns: 1fr;
    }
  }

  .empty-filter {
    text-align: center;
    padding: var(--space-8) var(--space-4);
    color: var(--grey-secondary);
  }

  .empty-filter p {
    margin: 0 0 var(--space-3) 0;
  }

  .reset-btn {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    color: var(--primary);
    font: inherit;
    font-size: var(--text-sm);
    padding: var(--space-2) var(--space-4);
    cursor: pointer;
  }

  .reset-btn:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
</style>
